import React from "react";
import { Translation, t } from "../../../core/translate";
import SharedLinks from "../shared-links";
import { connect } from "react-redux";
import { selectors as localeSelectors } from "../../../../redux/locale";
import { selectors as brandSelectors } from "../../../../redux/brand";
import {
  selectors as userSelectors,
  actions as userActions
} from "../../../../redux/user";
import {
  actions as routerActions,
  selectors as routerSelectors
} from "../../../../redux/router";
import Spinner from "../../../components/spinner";
import ContainerLayout from "../container-layout";
import { Button, Form } from "@vitalsource/vstui";
import { toastUtils } from "../../../../utils/toast-utils";
import Email from "./email";
import Password from "./password";
import Name from "./name";
import SecurityQuestions from "./security-questions";
import Terms from "./terms";
import { LockedEmail } from "../styles";
import Link from "../../../components/link";
import { ssoToOldOnlineURL, isRelativeURL } from "../../../utils/location";

class Registration extends React.Component {
  state = {};

  error = () => {
    toastUtils.notify(
      t(
        "registration.too-many-create-requests",
        "There was an error creating your account.  Please check your information and try again later."
      ),
      {
        type: "error"
      }
    );
  };

  submit = e => {
    e.preventDefault();
    if (this.props.completingAccount) {
      this.completeAccount(e.target);
    } else {
      this.createNewUser(e.target);
    }
  };

  getUserObject = form => {
    const {
      jigsawBrand,
      brandName,
      emailVerificationRoot,
      lockedEmail,
      currentLocale
    } = this.props;
    const email = lockedEmail || form.elements["user[email]"].value;
    const info = {
      jigsaw_brand: jigsawBrand,
      last_brand_accessed: jigsawBrand,
      bookshelf_brand: brandName,
      bookshelf_verification_url: emailVerificationRoot,
      bookshelf_verification_error_url: `${emailVerificationRoot}?expired=true`,
      email,
      password: form.elements["user[password]"].value,
      password_confirmation: form.elements["user[confirm-password]"].value,
      question_id: form.elements["user[question_id]"].value,
      question_response: form.elements["user[question_response]"].value,
      first_name: form.elements["user[first_name]"].value,
      last_name: form.elements["user[last_name]"].value,
      notify: false,
      locale: currentLocale
    };

    /* This should be the one form element that might not get rendered */
    const eulaAcceptedElement = form.elements["user[eula_accepted]"];
    if (eulaAcceptedElement) {
      info.eula_accepted = eulaAcceptedElement.checked;
    }

    return info;
  };

  redirect = userObject => {
    const { go, queryAfter } = this.props;
    const dest = queryAfter || "/";
    if (isRelativeURL(dest)) {
      go(dest);
    } else {
      ssoToOldOnlineURL(dest, false, userObject);
    }
  };

  completeAccount = form => {
    // Essentially just update user info
    const userObject = this.getUserObject(form);
    const { setInfo } = this.props;
    setInfo(userObject)
      .then(() => this.redirect(userObject))
      .catch(this.error);
  };

  createNewUser = async form => {
    const { createUser, emailExists } = this.props;
    const email = form.elements["user[email]"].value;
    this.setState({ submittedEmail: email });
    const emailStatus = await emailExists(email);
    if (!emailStatus.approved && !emailStatus.exists) {
      const userObject = this.getUserObject(form);
      // disabling for now because of BO-3947 - User in China cannot create Bookshelf accounts due to reCaptcha
      // This is old code using jquery, not replacing it yet because recaptcha is currently disabled for registration
      // userObject.recaptchaResponse = $(".g-recaptcha-response").val();
      createUser(userObject)
        .then(() => this.redirect(userObject))
        .catch(this.error);
    }
  };

  renderTitle() {
    if (this.props.lockedEmail) {
      return (
        <Translation id="sso.registration-heading">
          You're almost done!
        </Translation>
      );
    } else {
      return (
        <Translation
          id={
            this.props.brandName.indexOf("Vital") === 0
              ? "registration.registration-heading"
              : "registration.create-account"
          }
        >
          Create an Account
        </Translation>
      );
    }
  }

  renderRegistration() {
    const {
      registrationEnabled,
      completingAccount,
      userEmail,
      lockedEmail,
      emailLocked
    } = this.props;
    if (lockedEmail || registrationEnabled) {
      return (
        <Form onSubmit={this.submit} id="registration-form" method="post">
          {lockedEmail && (
            <LockedEmail>
              {emailLocked ? userEmail : lockedEmail}{" "}
              {!emailLocked ? (
                <Link href="user/decide" inline>
                  <Translation id="registration.change-email">
                    Change Email
                  </Translation>
                </Link>
              ) : null}
            </LockedEmail>
          )}
          <Name />
          {!lockedEmail && <Email submittedEmail={this.state.submittedEmail} />}
          <Password />
          <SecurityQuestions />
          <Terms />
          <Button id="submit-btn" fullWidth size="large">
            {completingAccount ? (
              <Translation id="base.finish">Finish</Translation>
            ) : (
              <Translation id="base.create">Create</Translation>
            )}
          </Button>
        </Form>
      );
    }
    return null;
  }

  render() {
    const { isUserFetching, creatingUser, completingAccount } = this.props;
    return (
      <ContainerLayout
        graphic="register"
        pageTitle={t("registration.create-account", "Create an Account")}
        title={this.renderTitle()}
      >
        {this.renderRegistration()}
        {!completingAccount && <SharedLinks signIn />}
        {(isUserFetching || creatingUser) && <Spinner />}
      </ContainerLayout>
    );
  }
}

const mapStateToProps = state => ({
  creatingUser: userSelectors.isCreatingUser(state),
  isCheckingEmailExists: userSelectors.isCheckingEmailExists(state),
  isUserFetching: userSelectors.isUserFetching(state),
  registrationEnabled: !brandSelectors.getBrandValue(
    state,
    "AccountCompletion.ssoOnly",
    false
  ),
  brandName: brandSelectors.getBrandValue(
    state,
    "brand",
    "VitalSource Bookshelf"
  ),
  jigsawBrand: brandSelectors.getBrandValue(
    state,
    "jigsawBrand",
    "vitalsource"
  ),
  completingAccount: userSelectors.isAuthenticated(state),
  userEmail: userSelectors.getUserEmail(state),
  lockedEmail:
    userSelectors.isAuthenticated(state) &&
    routerSelectors.getQueryParam(state, "email"),
  emailLocked: brandSelectors.getBrandValue(
    state,
    "Account.emailLocked",
    false
  ),
  queryAfter: routerSelectors.getQueryParam(state, "after"),
  emailVerificationRoot: `${routerSelectors.getRootRoute(state)}/verify`,
  currentLocale: localeSelectors.getCurrentLocale(state) || "en"
});

const mapDispatchToProps = dispatch => ({
  createUser: userObject => dispatch(userActions.createUser(userObject)),
  setInfo: userObject => dispatch(userActions.setInfo(userObject)),
  emailExists: email => dispatch(userActions.emailExists(email)),
  go: path => dispatch(routerActions.go(path))
});

const ConnectedRegistration = connect(
  mapStateToProps,
  mapDispatchToProps
)(Registration);

export default ConnectedRegistration;
